import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Mail, Megaphone, Palette, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { AdminSuccessBanner } from '@/components/shared/AdminSuccessBanner';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { composeRuleCopy, parseRuleCopy } from '@shared/email/commEmailView';
import {
    ACTIVATION_STEPS,
    RULE_ANCHORS,
    RULE_CHANNELS,
    RULE_SENDER_OPTIONS,
    RULE_SUCCESS,
} from './commCatalog';
import { TemplateEditorDialog } from './TemplateEditorDialog';
import { draftFromTemplate, emptyTemplateDraft, formatUnknownError, isRegistrationTemplate, templateSubject, type TemplateDraft } from './commUi';
import {
    audiencePredicate,
    cooldownHours,
    delayHours,
    emptyRuleDraft,
    RuleEditorDialog,
    type ConditionRow,
    type RuleDraft,
} from './RuleEditorDialog';
import { PunctualPanel } from './PunctualPanel';
import { EmailBrandPanel } from './EmailBrandPanel';
import { RegistrationFlowDialog, type FlowTemplate } from './RegistrationFlowDialog';
import { ChurnSurveyCard } from './ChurnSurveyCard';

type TabId = 'rules' | 'punctual' | 'templates' | 'brand';
type RuleOrigin = 'all' | 'registro' | 'otras';

function ruleSenderLabel(senderKey: string | null | undefined): string {
    return (
        RULE_SENDER_OPTIONS.find((s) => s.key === senderKey)?.label ??
        RULE_SENDER_OPTIONS[0].label
    );
}

interface CommRule {
    key: string;
    name: string;
    description: string | null;
    is_active: boolean;
    dry_run: boolean;
    anchor: string;
    channel: string;
    delay_hours: number;
    lookback_hours: number;
    cooldown_hours: number | null;
    ignores_cap: boolean;
    topic: string;
    audience_predicate: Record<string, unknown>;
    template_key: string;
    priority: number;
    success_event: string | null;
    success_window_hours: number | null;
    sender_key?: string | null;
    recurrence_days?: number | null;
    max_repeats?: number | null;
    month_day?: number | null;
    waiting: number;
    sent: number;
    dry_run_count: number;
    skipped: number;
    succeeded: number;
    opened: number;
    clicked: number;
    reserved_count?: number;
    expired_count?: number;
}

interface CommTemplate {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string | null;
    title: string | null;
    body: string;
    preheader: string | null;
    click_url: string | null;
    image_source: string | null;
    image_alt: string | null;
    is_active: boolean;
}

function pct(part: number, total: number): string {
    if (!total) return '—';
    return `${Math.round((Number(part) / Number(total)) * 100)}%`;
}

function ruleSentence(rule: CommRule): string {
    const hours = Number(rule.delay_hours);
    const wait = hours % 24 === 0 && hours >= 24 ? `${hours / 24} días` : `${hours} h`;
    const anchor = RULE_ANCHORS.find((a) => a.key === rule.anchor)?.label ?? rule.anchor;
    const channel = RULE_CHANNELS.find((c) => c.key === rule.channel)?.label ?? 'Correo';
    const goal = RULE_SUCCESS.find((s) => s.key === (rule.success_event || ''))?.label;
    const goalText =
        rule.success_event && goal
            ? ` Objetivo: ${goal.toLowerCase()} en ${Math.round(Number(rule.success_window_hours || 0) / 24)} días.`
            : '';
    const only = onlyEmailFromPredicate(rule.audience_predicate);
    const onlyText = only ? ` Solo a ${only}.` : '';
    const when =
        rule.anchor === 'month_start'
            ? `El día ${rule.month_day ?? 1} de cada mes (informe del mes anterior)`
            : rule.anchor === 'renewal_due'
              ? `${wait} antes de ${anchor}`
              : `A las ${wait} desde ${anchor}`;
    let recur = '';
    if (rule.recurrence_days && Number(rule.recurrence_days) > 0) {
        recur = ` Se repite cada ${Number(rule.recurrence_days)} días`;
        if (rule.max_repeats && Number(rule.max_repeats) > 0) {
            recur += ` (máx. ${Number(rule.max_repeats)} envíos)`;
        }
        recur += '.';
    }
    return `${channel}. ${when}.${onlyText}${goalText}${recur}`;
}

function onlyEmailFromPredicate(raw: Record<string, unknown> | null): string {
    const eq = (raw?.eq as Record<string, unknown> | undefined) ?? {};
    return typeof eq.account_email === 'string' ? eq.account_email : '';
}

type CommRuleJourney =
    | 'ONBOARDING'
    | 'PRO_TRIAL'
    | 'PRO_CONVERSION'
    | 'PRO_PAID'
    | 'AVISO'
    | 'OTRO';

const JOURNEY_ORDER: CommRuleJourney[] = [
    'ONBOARDING',
    'PRO_TRIAL',
    'PRO_CONVERSION',
    'PRO_PAID',
    'AVISO',
    'OTRO',
];

function classifyRuleJourney(rule: { key: string; name: string; channel: string }): CommRuleJourney {
    const hay = `${rule.key} ${rule.name}`.toUpperCase();
    if (hay.includes('ONBOARDING') || hay.startsWith('OB_')) return 'ONBOARDING';
    if (hay.includes('PRO_TRIAL') || hay.includes('TRIAL')) return 'PRO_TRIAL';
    if (hay.includes('PRO_CONVERSION') || hay.includes('CONVERSION')) return 'PRO_CONVERSION';
    if (hay.includes('PRO_PAID') || hay.includes('PAID')) return 'PRO_PAID';
    if (rule.channel === 'push' || hay.includes('AVISO') || hay.startsWith('PUSH_')) return 'AVISO';
    return 'OTRO';
}

function journeySortIndex(j: CommRuleJourney): number {
    const i = JOURNEY_ORDER.indexOf(j);
    return i < 0 ? 99 : i;
}

function conditionsFromPredicate(raw: Record<string, unknown> | null): ConditionRow[] {
    const predicate = raw ?? {};
    const rows: ConditionRow[] = [];
    for (const signal of (predicate.all as string[] | undefined) ?? []) {
        rows.push({ signal, op: 'is_true', value: '' });
    }
    for (const signal of (predicate.none as string[] | undefined) ?? []) {
        rows.push({ signal, op: 'is_false', value: '' });
    }
    const gte = (predicate.gte as Record<string, number> | undefined) ?? {};
    const lte = (predicate.lte as Record<string, number> | undefined) ?? {};
    const eq = (predicate.eq as Record<string, string> | undefined) ?? {};
    const inn = (predicate.in as Record<string, string[]> | undefined) ?? {};
    for (const [signal, value] of Object.entries(gte)) rows.push({ signal, op: 'gte', value: String(value) });
    for (const [signal, value] of Object.entries(lte)) rows.push({ signal, op: 'lte', value: String(value) });
    for (const [signal, value] of Object.entries(eq)) {
        if (signal === 'account_email') continue;
        rows.push({ signal, op: 'eq', value: String(value) });
    }
    for (const [signal, values] of Object.entries(inn)) {
        if (!Array.isArray(values) || values.length === 0) continue;
        rows.push({ signal, op: 'in', value: values.join(',') });
    }
    return rows;
}

function draftFromRule(rule: CommRule, template?: CommTemplate): RuleDraft {
    const hours = Number(rule.delay_hours);
    const asDays = hours % 24 === 0 && hours >= 24;
    const cool = Number(rule.cooldown_hours);
    const copy = parseRuleCopy(template?.body || '');
    return {
        key: rule.key,
        name: rule.name,
        description: rule.description || '',
        is_active: rule.is_active,
        dry_run: rule.dry_run,
        anchor: rule.anchor,
        channel: rule.channel === 'push' ? 'push' : 'email',
        delayAmount: String(asDays ? hours / 24 : hours),
        delayUnit: asDays ? 'days' : 'hours',
        lookbackHours: String(rule.lookback_hours ?? 48),
        priority: String(rule.priority ?? 100),
        topic: rule.topic || 'cuenta',
        cooldownDays: Number.isFinite(cool) && cool > 0 ? String(cool / 24) : '',
        ignoresCap: Boolean(rule.ignores_cap),
        conditions: conditionsFromPredicate(rule.audience_predicate),
        onlyEmail: onlyEmailFromPredicate(rule.audience_predicate),
        successEvent: rule.success_event || '',
        successDays: String(Math.max(1, Math.round(Number(rule.success_window_hours || 168) / 24))),
        subject: template?.subject || template?.title || '',
        preheader: template?.preheader || '',
        body: copy.text,
        imageUrl: template?.image_source || '',
        imageAlt: template?.image_alt || '',
        ctaLabel: copy.primaryLabel,
        click_url: template?.click_url || '',
        cta2Label: copy.secondaryLabel,
        cta2Url: copy.secondaryUrl,
        senderKey: 'alberto',
        recurrenceDays:
            rule.recurrence_days != null && Number(rule.recurrence_days) > 0
                ? String(rule.recurrence_days)
                : '',
        maxRepeats:
            rule.max_repeats != null && Number(rule.max_repeats) > 0 ? String(rule.max_repeats) : '',
        monthDay:
            rule.month_day != null && Number(rule.month_day) >= 1
                ? String(Math.min(28, Number(rule.month_day)))
                : '1',
    };
}

export function CommunicationsPage() {
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<TabId>('rules');
    const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
    const [creatingRule, setCreatingRule] = useState(false);
    const [deleteKey, setDeleteKey] = useState<string | null>(null);
    const [templateDraft, setTemplateDraft] = useState<TemplateDraft | null>(null);
    const [creatingTemplate, setCreatingTemplate] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [ruleSearch, setRuleSearch] = useState('');
    const [ruleChannel, setRuleChannel] = useState<'all' | 'email' | 'push'>('all');
    const [ruleStatus, setRuleStatus] = useState<'all' | 'sending' | 'testing' | 'paused'>('all');
    const [ruleOrigin, setRuleOrigin] = useState<RuleOrigin>('all');
    const [ruleJourney, setRuleJourney] = useState<'all' | CommRuleJourney>('all');
    const [flowStep, setFlowStep] = useState<(typeof ACTIVATION_STEPS)[number]['id'] | null>(null);

    const rulesQuery = useQuery({
        queryKey: ['admin', 'comm-rules'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<CommRule[]>('admin_list_comm_rules');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const templatesQuery = useQuery({
        queryKey: ['admin', 'comm-templates'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<CommTemplate[]>('admin_get_comm_templates');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const statsQuery = useQuery({
        queryKey: ['admin', 'activation-stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<
                Array<{ template_key: string; sent: number; measured: number; opened: number; clicked: number }>
            >('admin_list_activation_stats');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const rules = rulesQuery.data ?? [];
    const templates = templatesQuery.data ?? [];

    const visibleRules = useMemo(() => {
        if (ruleOrigin === 'registro') return [];
        const needle = ruleSearch.trim().toLowerCase();
        const filtered = rules.filter((rule) => {
            const status = !rule.is_active ? 'paused' : rule.dry_run ? 'testing' : 'sending';
            if (ruleChannel !== 'all' && rule.channel !== ruleChannel) return false;
            if (ruleStatus !== 'all' && status !== ruleStatus) return false;
            if (ruleJourney !== 'all' && classifyRuleJourney(rule) !== ruleJourney) return false;
            if (!needle) return true;
            return `${rule.name} ${rule.key} ${ruleSentence(rule)}`.toLowerCase().includes(needle);
        });
        return [...filtered].sort((a, b) => {
            const ja = journeySortIndex(classifyRuleJourney(a));
            const jb = journeySortIndex(classifyRuleJourney(b));
            if (ja !== jb) return ja - jb;
            const da = Number(a.delay_hours);
            const db = Number(b.delay_hours);
            if (da !== db) return da - db;
            return a.name.localeCompare(b.name, 'es');
        });
    }, [ruleChannel, ruleJourney, ruleOrigin, ruleSearch, ruleStatus, rules]);
    const registrationRows = useMemo(() => {
        if (ruleOrigin === 'otras') return [];
        if (ruleChannel === 'push') return [];
        if (ruleStatus === 'sending' || ruleStatus === 'testing') return [];
        const needle = ruleSearch.trim().toLowerCase();
        const stats = statsQuery.data ?? [];
        return ACTIVATION_STEPS.filter((step) => {
            if (!needle) return true;
            return `${step.title} ${step.when} registro`.toLowerCase().includes(needle);
        }).map((step) => {
            const rows = stats.filter((stat) => stat.template_key.startsWith(step.prefix));
            const total = (field: 'sent' | 'measured' | 'opened' | 'clicked') =>
                rows.reduce((acc, row) => acc + Number(row[field] || 0), 0);
            return {
                step,
                sent: total('sent'),
                measured: total('measured'),
                opened: total('opened'),
                clicked: total('clicked'),
            };
        });
    }, [ruleChannel, ruleOrigin, ruleSearch, ruleStatus, statsQuery.data]);

    const showSuccess = (message: string) => {
        setPageError(null);
        setSuccessMessage(message);
    };

    const saveRule = useMutation({
        mutationFn: async (args: { draft: RuleDraft; creating: boolean }) => {
            const draft = args.draft;
            const only = draft.onlyEmail.trim();
            if (only && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(only.toLowerCase())) {
                throw new Error('El correo de la prueba no es válido');
            }
            const key = draft.key.trim();
            const channel = draft.channel === 'push' ? 'push' : 'email';
            const subject = draft.subject.trim() || null;
            const body = composeRuleCopy({
                text: draft.body,
                primaryLabel: channel === 'push' ? '' : draft.ctaLabel,
                secondaryLabel: channel === 'push' ? '' : draft.cta2Label,
                secondaryUrl: channel === 'push' ? '' : draft.cta2Url,
            });
            const image = draft.imageUrl.trim() || null;
            const existing = templates.find(
                (t) => t.key === key && t.channel === channel && t.locale === 'es' && t.variant === 'A'
            );
            if (!existing) {
                const created = await dbClient.rpc('admin_create_comm_template', {
                    p_key: key,
                    p_channel: channel,
                    p_body: body,
                    p_locale: 'es',
                    p_variant: 'A',
                    p_subject: subject,
                    p_title: subject,
                    p_preheader: draft.preheader.trim() || null,
                    p_click_url: draft.click_url.trim() || null,
                    p_image_source: image,
                    p_image_alt: draft.imageAlt.trim() || null,
                    p_is_active: true,
                });
                if (created.error) throw created.error;
            } else {
                const updated = await dbClient.rpc('admin_update_comm_template', {
                    p_key: key,
                    p_channel: channel,
                    p_locale: 'es',
                    p_variant: 'A',
                    p_body: body,
                    p_subject: subject,
                    p_title: subject,
                    p_preheader: draft.preheader.trim() || null,
                    p_click_url: draft.click_url.trim() || null,
                    p_image_source: image,
                    p_image_alt: draft.imageAlt.trim() || null,
                    p_is_active: true,
                });
                if (updated.error) throw updated.error;
            }
            const successHours = draft.successEvent ? Number(draft.successDays) * 24 : null;
            const saved = await dbClient.rpc('admin_upsert_comm_rule', {
                p_key: key,
                p_name: draft.name.trim(),
                p_description: draft.description.trim() || null,
                p_is_active: draft.is_active,
                p_dry_run: draft.dry_run,
                p_anchor: draft.anchor,
                p_delay_hours: delayHours(draft),
                p_lookback_hours: Number(draft.lookbackHours) || 48,
                p_audience_predicate: audiencePredicate(draft.conditions, draft.onlyEmail),
                p_template_key: key,
                p_priority: Number(draft.priority) || 100,
                p_message_class: 'activacion',
                p_topic: draft.topic || 'cuenta',
                p_success_event: draft.successEvent || null,
                p_success_window_hours: successHours,
                p_channel: channel,
                p_cooldown_hours: cooldownHours(draft),
                p_ignores_cap: draft.ignoresCap,
                p_sender_key: 'alberto',
                p_recurrence_days: (() => {
                    const n = Number(draft.recurrenceDays);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                p_max_repeats: (() => {
                    const n = Number(draft.maxRepeats);
                    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
                })(),
                p_month_day:
                    draft.anchor === 'month_start'
                        ? Math.max(1, Math.min(28, Math.round(Number(draft.monthDay) || 1)))
                        : null,
            });
            if (saved.error) throw saved.error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-rules'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-templates'] });
            setRuleDraft(null);
            showSuccess('Regla guardada');
        },
    });

    const setFlags = useMutation({
        mutationFn: async (args: { key: string; is_active?: boolean; dry_run?: boolean }) => {
            const { error } = await dbClient.rpc('admin_set_comm_rule_flags', {
                p_key: args.key,
                p_is_active: args.is_active ?? null,
                p_dry_run: args.dry_run ?? null,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-rules'] });
            showSuccess('Regla actualizada');
        },
    });

    const deleteRule = useMutation({
        mutationFn: async (key: string) => {
            const { error } = await dbClient.rpc('admin_delete_comm_rule', { p_key: key });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-rules'] });
            setDeleteKey(null);
            showSuccess('Regla eliminada');
        },
    });

    const saveTemplate = useMutation({
        mutationFn: async (args: { draft: TemplateDraft; creating: boolean }) => {
            const draft = args.draft;
            const subject = draft.subject.trim() || null;
            if (args.creating) {
                const { error } = await dbClient.rpc('admin_create_comm_template', {
                    p_key: draft.key.trim(),
                    p_channel: draft.channel,
                    p_body: draft.body,
                    p_locale: draft.locale || 'es',
                    p_variant: draft.variant || 'A',
                    p_subject: subject,
                    p_title: subject,
                    p_preheader: draft.preheader.trim() || null,
                    p_click_url: draft.click_url.trim() || null,
                    p_is_active: draft.is_active,
                });
                if (error) throw error;
                return;
            }
            const { error } = await dbClient.rpc('admin_update_comm_template', {
                p_key: draft.key,
                p_channel: draft.channel,
                p_locale: draft.locale,
                p_variant: draft.variant,
                p_body: draft.body,
                p_subject: subject,
                p_title: subject,
                p_preheader: draft.preheader.trim() || null,
                p_click_url: draft.click_url.trim() || null,
                p_is_active: draft.is_active,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-templates'] });
            setTemplateDraft(null);
            showSuccess('Plantilla guardada');
        },
    });

    return (
        <div className="space-y-4">
            <PageHeader
                title={
                    tab === 'punctual'
                        ? 'Puntuales'
                        : tab === 'templates'
                          ? 'Plantillas'
                          : tab === 'brand'
                            ? 'Aspecto del correo'
                            : 'Reglas'
                }
                description={
                    tab === 'rules'
                        ? 'El registro usa Alberto (alberto@news, responde hola@). En cada regla eliges remitente. Como máximo dos correos por semana y un aviso al día.'
                        : tab === 'brand'
                          ? 'Logo, color, firma y pie de todos los correos.'
                          : 'Campañas a mano, plantillas y el marco común.'
                }
            >
                {tab === 'rules' && (
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
                        onClick={() => {
                            saveRule.reset();
                            setCreatingRule(true);
                            setRuleDraft(emptyRuleDraft());
                        }}
                    >
                        <Plus size={16} /> Nueva regla
                    </button>
                )}
            </PageHeader>

            {successMessage && <AdminSuccessBanner message={successMessage} onDismiss={() => setSuccessMessage(null)} />}
            {pageError && <p className="text-sm text-destructive">{pageError}</p>}

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${tab === 'rules' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => setTab('rules')}
                >
                    <Mail size={16} /> Reglas
                </button>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${tab === 'punctual' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => setTab('punctual')}
                >
                    <Megaphone size={16} /> Puntuales
                </button>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${tab === 'templates' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => setTab('templates')}
                >
                    <FileText size={16} /> Plantillas
                </button>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${tab === 'brand' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => setTab('brand')}
                >
                    <Palette size={16} /> Aspecto
                </button>
            </div>

            {tab === 'rules' && (
                <div className="space-y-4">
                    {(ruleOrigin === 'registro' || ruleOrigin === 'all') && (
                        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                            <p className="text-sm font-medium">Bienvenida, Día 2, Día 4 y Día 7 están retirados</p>
                            <p className="text-xs text-muted-foreground">
                                Siguen en la tabla por las cifras y el texto que se envió. La bienvenida vigente sale por las reglas.
                            </p>
                        </div>
                    )}
                    {(ruleOrigin === 'all' || ruleOrigin === 'otras') && <ChurnSurveyCard />}
                    <div className="flex flex-wrap gap-2">
                        {(
                            [
                                ['all', 'Todas'],
                                ['sending', 'Enviando'],
                                ['testing', 'En prueba'],
                                ['paused', 'Pausadas'],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={cn(
                                    'rounded-full border px-3 py-1.5 text-sm',
                                    ruleStatus === value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
                                )}
                                onClick={() => setRuleStatus(value)}
                            >
                                {label}
                            </button>
                        ))}
                        {(
                            [
                                ['all', 'Canal'],
                                ['email', 'Correo'],
                                ['push', 'Aviso'],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={cn(
                                    'rounded-full border px-3 py-1.5 text-sm',
                                    ruleChannel === value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
                                )}
                                onClick={() => setRuleChannel(value)}
                            >
                                {label}
                            </button>
                        ))}
                        {(
                            [
                                ['all', 'Registro y demás'],
                                ['registro', 'Del registro'],
                                ['otras', 'Las demás'],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={cn(
                                    'rounded-full border px-3 py-1.5 text-sm',
                                    ruleOrigin === value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
                                )}
                                onClick={() => setRuleOrigin(value)}
                            >
                                {label}
                            </button>
                        ))}
                        {(
                            [
                                ['all', 'Journey'],
                                ['ONBOARDING', 'Onboarding'],
                                ['PRO_TRIAL', 'Pro trial'],
                                ['PRO_CONVERSION', 'Pro conversión'],
                                ['PRO_PAID', 'Pro pago'],
                                ['AVISO', 'Aviso'],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={cn(
                                    'rounded-full border px-3 py-1.5 text-sm',
                                    ruleJourney === value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
                                )}
                                onClick={() => setRuleJourney(value)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="relative max-w-md">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={ruleSearch}
                            onChange={(e) => setRuleSearch(e.target.value)}
                            placeholder="Buscar por nombre"
                            className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                        />
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-border">
                        {rulesQuery.isLoading && (
                            <div className="flex justify-center p-10">
                                <Loader2 className="animate-spin text-muted-foreground" />
                            </div>
                        )}
                        {rulesQuery.isError && (
                            <p className="p-4 text-sm text-destructive">
                                {formatUnknownError(rulesQuery.error, 'No se pudieron cargar las reglas')}
                            </p>
                        )}
                        {!rulesQuery.isLoading && !rulesQuery.isError && (
                            <table className="w-full min-w-[1180px] text-sm">
                                <thead className="bg-muted/40 text-left text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 font-medium">Nombre</th>
                                        <th className="px-4 py-3 font-medium">Quién envía</th>
                                        <th className="px-4 py-3 font-medium">Canal</th>
                                        <th className="px-4 py-3 font-medium">Cuándo</th>
                                        <th className="px-4 py-3 font-medium">Estado</th>
                                        <th className="px-4 py-3 font-medium text-right">En espera</th>
                                        <th className="px-4 py-3 font-medium text-right">Enviados</th>
                                        <th className="px-4 py-3 font-medium text-right">Apertura</th>
                                        <th className="px-4 py-3 font-medium text-right">Clic</th>
                                        <th
                                            className="px-4 py-3 font-semibold text-right text-foreground"
                                            title="Objetivo conseguido / enviados"
                                        >
                                            Objetivo
                                        </th>
                                        <th
                                            className="px-4 py-3 font-medium text-right"
                                            title="Reservas vigentes (en prueba + en espera)"
                                        >
                                            Reservas
                                        </th>
                                        <th
                                            className="px-4 py-3 font-medium text-right"
                                            title="Caducadas al salir de En prueba (fuera de ventana)"
                                        >
                                            Caducadas
                                        </th>
                                        <th className="px-4 py-3 font-medium">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRules.length === 0 && registrationRows.length === 0 && (
                                        <tr>
                                            <td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">
                                                {rules.length === 0 && ruleOrigin !== 'registro'
                                                    ? 'Todavía no has creado reglas. Las del registro salen con el filtro Del registro.'
                                                    : 'Ninguna regla coincide con el filtro.'}
                                            </td>
                                        </tr>
                                    )}
                                    {registrationRows.map(({ step, sent, measured, opened, clicked }) => (
                                        <tr key={step.id} className="border-t border-border">
                                            <td className="px-4 py-3 font-medium">{step.title}</td>
                                            <td className="px-4 py-3">{step.from}</td>
                                            <td className="px-4 py-3">Correo</td>
                                            <td className="max-w-[280px] truncate px-4 py-3 text-muted-foreground" title={step.when}>
                                                {step.when}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status="paused" label="Retirado" variant="neutral" size="sm" />
                                            </td>
                                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                                            <td className="px-4 py-3 text-right">{sent}</td>
                                            <td className="px-4 py-3 text-right">{measured ? pct(opened, measured) : '—'}</td>
                                            <td className="px-4 py-3 text-right">{pct(clicked, sent)}</td>
                                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    className="text-primary"
                                                    onClick={() => setFlowStep(step.id)}
                                                >
                                                    Ver
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {visibleRules.map((rule) => {
                                        const sent = Number(rule.sent);
                                        const status = !rule.is_active ? 'paused' : rule.dry_run ? 'testing' : 'sending';
                                        const label = !rule.is_active ? 'Pausada' : rule.dry_run ? 'En prueba' : 'Enviando';
                                        const sentence = ruleSentence(rule);
                                        return (
                                            <tr key={rule.key} className="border-t border-border">
                                                <td className="px-4 py-3 font-medium">{rule.name}</td>
                                                <td className="px-4 py-3">
                                                    {rule.channel === 'push'
                                                        ? 'Aviso en el móvil'
                                                        : ruleSenderLabel(rule.sender_key)}
                                                </td>
                                                <td className="px-4 py-3">{rule.channel === 'push' ? 'Aviso' : 'Correo'}</td>
                                                <td className="max-w-[280px] truncate px-4 py-3 text-muted-foreground" title={sentence}>
                                                    {sentence}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge
                                                        status={status}
                                                        label={label}
                                                        variant={status === 'sending' ? 'success' : status === 'testing' ? 'warning' : 'neutral'}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right">{Number(rule.waiting)}</td>
                                                <td className="px-4 py-3 text-right">{sent}</td>
                                                <td className="px-4 py-3 text-right">{pct(rule.opened, sent)}</td>
                                                <td className="px-4 py-3 text-right">{pct(rule.clicked, sent)}</td>
                                                <td className="px-4 py-3 text-right text-base font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                                                    {pct(rule.succeeded, sent)}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    {Number(
                                                        rule.reserved_count ??
                                                            Number(rule.dry_run_count) + Number(rule.waiting)
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">{Number(rule.expired_count ?? 0)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap items-center gap-2 whitespace-nowrap">
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-1 text-primary"
                                                            onClick={() => {
                                                                saveRule.reset();
                                                                setCreatingRule(false);
                                                                setRuleDraft(
                                                                    draftFromRule(
                                                                        rule,
                                                                        templates.find(
                                                                            (t) =>
                                                                                t.key === rule.template_key &&
                                                                                t.channel === (rule.channel === 'push' ? 'push' : 'email')
                                                                        )
                                                                    )
                                                                );
                                                            }}
                                                        >
                                                            <Pencil size={14} /> Editar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setFlags.mutate({ key: rule.key, is_active: !rule.is_active })}
                                                        >
                                                            {rule.is_active ? 'Pausar' : 'Encender'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setFlags.mutate({ key: rule.key, dry_run: !rule.dry_run })}
                                                        >
                                                            {rule.dry_run ? 'De verdad' : 'A prueba'}
                                                        </button>
                                                        <button type="button" className="text-destructive" onClick={() => setDeleteKey(rule.key)}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {tab === 'punctual' && <PunctualPanel />}

            {tab === 'brand' && <EmailBrandPanel />}

            {tab === 'templates' && (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                    <div className="flex items-center justify-between border-b border-border p-3">
                        <p className="text-sm text-muted-foreground">El correo de una regla se edita en la propia regla. Los del registro se abren en Reglas, con Ver.</p>
                        <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm text-primary"
                            onClick={() => {
                                setCreatingTemplate(true);
                                setTemplateDraft(emptyTemplateDraft());
                            }}
                        >
                            <Plus size={14} /> Nueva plantilla
                        </button>
                    </div>
                    <table className="w-full text-sm">
                        <tbody>
                            {templates.filter((t) => !isRegistrationTemplate(t.key)).map((t) => (
                                <tr key={`${t.key}-${t.channel}-${t.variant}`} className="border-t border-border/60">
                                    <td className="p-3 font-mono text-xs">{t.key}</td>
                                    <td>{t.channel}</td>
                                    <td className="max-w-[280px] truncate">{templateSubject(t) || '—'}</td>
                                    <td className="p-3 text-right">
                                        <button
                                            type="button"
                                            className="text-xs text-primary"
                                            onClick={() => {
                                                setCreatingTemplate(false);
                                                setTemplateDraft(draftFromTemplate(t));
                                            }}
                                        >
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {ruleDraft && (
                <RuleEditorDialog
                    creating={creatingRule}
                    draft={ruleDraft}
                    templates={templates}
                    saving={saveRule.isPending}
                    error={saveRule.isError ? formatUnknownError(saveRule.error, 'No se pudo guardar') : null}
                    onChange={setRuleDraft}
                    onCancel={() => setRuleDraft(null)}
                    onSave={() => saveRule.mutate({ draft: ruleDraft, creating: creatingRule })}
                />
            )}

            {templateDraft && (
                <TemplateEditorDialog
                    creating={creatingTemplate}
                    draft={templateDraft}
                    templates={templates}
                    saving={saveTemplate.isPending}
                    error={saveTemplate.isError ? formatUnknownError(saveTemplate.error, 'No se pudo guardar') : null}
                    onChange={setTemplateDraft}
                    onCancel={() => setTemplateDraft(null)}
                    onSave={() => saveTemplate.mutate({ draft: templateDraft, creating: creatingTemplate })}
                />
            )}

            {flowStep && (
                <RegistrationFlowDialog
                    key={`${flowStep}:${templates.length}`}
                    stepId={flowStep}
                    templates={templates as FlowTemplate[]}
                    onClose={() => setFlowStep(null)}
                />
            )}

            <AdminConfirmDialog
                open={!!deleteKey}
                title="Eliminar regla"
                description="Se borra la regla y la cola de quien aún no la ha recibido. Los envíos ya hechos se quedan en el histórico."
                confirmLabel="Eliminar"
                variant="danger"
                onCancel={() => setDeleteKey(null)}
                onConfirm={() => deleteKey && deleteRule.mutate(deleteKey)}
            />
        </div>
    );
}

